/**
 * Exercise picker modal. Ported verbatim from the original app's
 * ExercisePicker.tsx — same UI, with the api client for data loading.
 * Reads GET /api/workouts/exercises.
 */

import { useEffect, useState } from 'react';
import { Search, X, Plus } from 'lucide-react';
import { api } from '@/lib/api';

interface Exercise {
  id: string;
  name: string;
  category: string;
  muscle_group: string;
  equipment: string | null;
  video_url?: string | null;
}

const muscleFilters = [
  'All', 'Chest', 'Back', 'Shoulders', 'Biceps', 'Triceps',
  'Quads', 'Hamstrings', 'Glutes', 'Calves', 'Abs',
];

interface ExercisePickerProps {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  onSelect: (exercise: Exercise) => void;
}

export function ExercisePicker({ title, isOpen, onClose, onSelect }: ExercisePickerProps) {
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [muscleFilter, setMuscleFilter] = useState('All');

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    api
      .get<{ exercises: Exercise[] }>('/api/workouts/exercises')
      .then((data) => setExercises(data.exercises || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isOpen]);

  if (!isOpen) return null;

  const filtered = exercises.filter((ex) => {
    if (muscleFilter !== 'All' && ex.muscle_group?.toLowerCase() !== muscleFilter.toLowerCase()) return false;
    if (search) {
      const q = search.toLowerCase();
      return ex.name.toLowerCase().includes(q) || ex.muscle_group?.toLowerCase().includes(q);
    }
    return true;
  });

  function handleSelect(exercise: Exercise) {
    onSelect(exercise);
    setSearch('');
    setMuscleFilter('All');
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center md:p-4" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="flex flex-col w-full max-w-full min-w-0 h-full md:w-[560px] md:max-w-[calc(100vw-2rem)] md:h-[80vh] md:max-h-[calc(100vh-2rem)] md:rounded-2xl overflow-hidden" style={{ background: 'var(--navy-dark)', border: '1px solid rgba(255,255,255,0.1)' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-[max(16px,env(safe-area-inset-top))] md:pt-4 pb-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <h2 className="text-lg font-semibold text-white">{title}</h2>
        <button onClick={onClose} style={{ color: 'var(--gold)' }}>
          <X size={22} />
        </button>
      </div>

      {/* Search */}
      <div className="px-5 pt-3 pb-2">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--gray-500)' }} />
          <input
            type="text"
            className="input"
            style={{ paddingLeft: 36, paddingRight: search ? 36 : undefined }}
            placeholder="Search exercises..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2"
              style={{ color: 'var(--gray-500)' }}
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Muscle Filter Chips — wrap onto multiple rows so every pill stays
          visible at any modal width (half-monitor desktop included). No
          horizontal scroll means nothing gets clipped off the right edge. */}
      <div className="flex flex-wrap gap-1.5 px-5 pb-3 min-w-0 max-w-full">
        {muscleFilters.map((filter) => (
          <button
            key={filter}
            onClick={() => setMuscleFilter(filter)}
            className="whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium transition-all flex-shrink-0"
            style={{
              background: muscleFilter === filter ? 'var(--gold)' : 'var(--surface)',
              color: muscleFilter === filter ? 'var(--navy)' : 'var(--gray-400)',
              border: muscleFilter === filter ? '1px solid var(--gold)' : '1px solid rgba(255,255,255,0.08)',
            }}
          >
            {filter}
          </button>
        ))}
      </div>

      {/* Exercise List */}
      <div className="flex-1 overflow-y-auto px-5 pb-6">
        {loading ? (
          <div className="flex flex-col gap-2 pt-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-14 rounded-lg animate-pulse" style={{ background: 'var(--surface)' }} />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-sm" style={{ color: 'var(--gray-500)' }}>No exercises found</p>
          </div>
        ) : (
          <div className="flex flex-col">
            {filtered.map((exercise) => (
              <button
                key={exercise.id}
                onClick={() => handleSelect(exercise)}
                className="flex items-center justify-between py-3 text-left transition-colors hover:bg-white/5"
                style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
              >
                <div>
                  <p className="text-sm text-white">{exercise.name}</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="text-xs" style={{ color: 'var(--gray-500)' }}>{exercise.muscle_group}</span>
                    {exercise.equipment && (
                      <span className="text-xs" style={{ color: 'var(--gray-500)' }}>&middot; {exercise.equipment}</span>
                    )}
                  </div>
                </div>
                <Plus size={18} style={{ color: 'var(--gold)' }} />
              </button>
            ))}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
